import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PurchaseReturns from "@/components/PurchaseReturns";
import { toast } from "sonner";
import { Boxes, FileText, Plus, ReceiptText, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_FORM = {
  outlet_id: "",
  supplier: "",
  purchase_type: "stock",
  supply_scheme: "buy",
  payment_method: "payable",
  reference_no: "",
  expense_account_id: "",
  expense_category: "",
  expense_description: "",
  expense_amount: "",
  note: "",
  items: [],
};

const PAYMENT_LABELS = {
  consignment: "Titip jual / Konsinyasi",
  payable: "Utang",
  cash: "Tunai",
  bank: "Bank / Transfer",
};

export default function Purchases() {
  const { user, hasPerm } = useAuth();
  const canReturn = ["admin", "supervisor"].includes(user?.role);
  const [returnTarget, setReturnTarget] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const [purchaseResponse, productResponse, outletResponse, expenseResponse] = await Promise.all([
        api.get("/purchases"),
        api.get("/products"),
        api.get("/outlets"),
        api.get("/purchases/expense-accounts"),
      ]);
      setPurchases(purchaseResponse.data || []);
      setProducts((productResponse.data || []).filter((row) => row.active !== false));
      setOutlets((outletResponse.data || []).filter((row) => row.active !== false));
      setExpenseAccounts(expenseResponse.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Data pembelian gagal dimuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const outletProducts = useMemo(() => (
    products.filter((product) => !form.outlet_id || !product.outlet_id || product.outlet_id === form.outlet_id)
  ), [products, form.outlet_id]);

  const selectedExpense = useMemo(() => (
    expenseAccounts.find((account) => account.id === form.expense_account_id)
  ), [expenseAccounts, form.expense_account_id]);

  const stockTotal = useMemo(() => (
    form.items.reduce((sum, item) => sum + (Number(item.qty || 0) * Number(item.cost || 0)), 0)
  ), [form.items]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, outlet_id: outlets[0]?.id || "" });
    setOpen(true);
  };

  const setPurchaseType = (purchaseType) => {
    setForm((current) => ({
      ...current,
      purchase_type: purchaseType,
      supply_scheme: purchaseType === "non_stock" ? "buy" : current.supply_scheme,
      items: purchaseType === "stock" ? current.items : [],
      expense_account_id: purchaseType === "non_stock" ? current.expense_account_id : "",
      expense_category: purchaseType === "non_stock" ? current.expense_category : "",
      expense_description: purchaseType === "non_stock" ? current.expense_description : "",
      expense_amount: purchaseType === "non_stock" ? current.expense_amount : "",
    }));
  };

  const addItem = () => setForm((current) => ({
    ...current,
    items: [...current.items, { product_id: "", name: "", qty: 1, cost: 0 }],
  }));

  const updateItem = (index, key, value) => setForm((current) => ({
    ...current,
    items: current.items.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const product = key === "product_id" ? products.find((row) => row.id === value) : null;
      return {
        ...item,
        [key]: value,
        ...(product ? { name: product.name || "", cost: Number(product.cost || 0) } : {}),
      };
    }),
  }));

  const removeItem = (index) => setForm((current) => ({
    ...current,
    items: current.items.filter((_, itemIndex) => itemIndex !== index),
  }));

  const save = async () => {
    if (!form.outlet_id) return toast.error("Pilih outlet");
    if (form.supply_scheme === "consignment" && !form.supplier.trim()) return toast.error("Isi vendor penitip barang");
    if (form.purchase_type === "stock" && form.items.length === 0) return toast.error("Tambahkan minimal satu item stok");
    if (form.purchase_type === "stock" && form.items.some((item) => !item.product_id || Number(item.qty) <= 0 || Number(item.cost) < 0)) {
      return toast.error("Lengkapi produk, qty, dan harga beli");
    }
    if (form.purchase_type === "non_stock" && !form.expense_account_id) return toast.error("Pilih akun beban");
    if (form.purchase_type === "non_stock" && Number(form.expense_amount) <= 0) return toast.error("Nominal biaya harus lebih dari 0");
    if (form.purchase_type === "non_stock" && !form.expense_description.trim()) return toast.error("Isi keterangan biaya");

    const items = form.purchase_type === "stock"
      ? form.items.map((item) => ({
          ...item,
          qty: Number(item.qty),
          cost: Number(item.cost),
          subtotal: Number(item.qty) * Number(item.cost),
        }))
      : [];

    const payload = {
      ...form,
      items,
      expense_amount: form.purchase_type === "non_stock" ? Number(form.expense_amount) : null,
      expense_category: form.purchase_type === "non_stock"
        ? (selectedExpense?.system_key || selectedExpense?.name || form.expense_category || "")
        : "",
    };

    setSaving(true);
    try {
      await api.post("/purchases", payload);
      toast.success(form.purchase_type === "stock"
        ? (form.supply_scheme === "consignment" ? "Barang titipan dicatat dan stok bertambah" : "Pembelian stok dicatat dan persediaan bertambah")
        : "Biaya non-stok dicatat tanpa mengubah persediaan");
      setOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pembelian gagal disimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="overline text-zinc-500">PEMBELIAN & BIAYA</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Pembelian</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">
            Catat pembelian barang stok maupun biaya non-stok seperti listrik, air, sewa, internet, maintenance, dan biaya operasional lainnya.
          </p>
        </div>
        <button hidden={!hasPerm("purchases.create")} onClick={openCreate} data-testid="purchase-add-btn" className="inline-flex items-center gap-2 bg-[#002FA7] px-4 py-2.5 text-sm font-semibold text-white">
          <Plus className="h-4 w-4" /> Catat Pembelian
        </button>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-0 border border-border bg-white lg:grid-cols-4">
        <div className="p-4"><div className="overline text-zinc-500">Total Transaksi</div><div className="mt-1 text-2xl font-black">{purchases.length}</div></div>
        <div className="border-l border-border p-4"><div className="overline text-zinc-500">Pembelian Stok</div><div className="mt-1 text-2xl font-black">{purchases.filter((row) => (row.purchase_type || "stock") !== "non_stock").length}</div></div>
        <div className="border-l border-border p-4"><div className="overline text-zinc-500">Biaya Non-Stok</div><div className="mt-1 text-2xl font-black">{purchases.filter((row) => row.purchase_type === "non_stock").length}</div></div>
        <div className="border-l border-border p-4"><div className="overline text-zinc-500">Total Non-Stok</div><div className="mt-1 text-lg font-black">{formatIDR(purchases.filter((row) => row.purchase_type === "non_stock").reduce((sum, row) => sum + Number(row.total || 0), 0))}</div></div>
      </div>

      <div className="overflow-x-auto border border-border bg-white">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b border-border bg-zinc-50"><tr>
            <th className="p-3 text-left overline">Waktu</th>
            <th className="p-3 text-left overline">Outlet</th>
            <th className="p-3 text-left overline">Jenis</th>
            <th className="p-3 text-left overline">Supplier / Vendor</th>
            <th className="p-3 text-left overline">Keterangan</th>
            <th className="p-3 text-left overline">Pembayaran</th>
            <th className="p-3 text-right overline">Total</th>
            <th className="p-3 text-left overline">Retur</th>
          </tr></thead>
          <tbody>
            {!loading && purchases.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-zinc-500">Belum ada pembelian</td></tr>}
            {purchases.map((purchase) => {
              const nonStock = purchase.purchase_type === "non_stock";
              const expense = purchase.expense_account || {};
              return (
                <tr key={purchase.id} className="border-b border-border hover:bg-zinc-50">
                  <td className="p-3 text-xs text-zinc-500">{formatDate(purchase.created_at)}</td>
                  <td className="p-3">{outlets.find((outlet) => outlet.id === purchase.outlet_id)?.name || "-"}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${nonStock ? "border-violet-200 bg-violet-50 text-violet-700" : "border-blue-200 bg-blue-50 text-blue-700"}`}>
                      {nonStock ? <ReceiptText className="h-3 w-3" /> : <Boxes className="h-3 w-3" />}
                      {nonStock ? "Non-Stok" : purchase.supply_scheme === "consignment" ? "Titip Jual" : "Beli"}
                    </span>
                  </td>
                  <td className="p-3">{purchase.supplier || "-"}</td>
                  <td className="p-3">
                    {nonStock ? (
                      <div><div className="font-semibold">{expense.code ? `${expense.code} - ` : ""}{expense.name || purchase.expense_category || "Beban"}</div><div className="mt-0.5 text-xs text-zinc-500">{purchase.expense_description || purchase.note || "-"}</div></div>
                    ) : (
                      <span className="text-zinc-500">{(purchase.items || []).length} item</span>
                    )}
                  </td>
                  <td className="p-3">{PAYMENT_LABELS[purchase.payment_method || "payable"] || purchase.payment_method || "Utang"}</td>
                  <td className="p-3 text-right font-semibold tabular-nums">{formatIDR(purchase.total)}</td>
                  <td className="p-3">{canReturn && !nonStock && (!purchase.status || purchase.status === "completed") && <button onClick={() => setReturnTarget(purchase)} className="text-blue-700 underline">Ajukan Retur</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PurchaseReturns target={returnTarget} onClose={() => setReturnTarget(null)} canReturn={canReturn} onChanged={load} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl rounded-none">
          <DialogHeader><DialogTitle>Catat Pembelian</DialogTitle></DialogHeader>

          <div className="grid grid-cols-2 gap-2 border border-border bg-zinc-50 p-1">
            <button type="button" onClick={() => setPurchaseType("stock")} className={`flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold ${form.purchase_type === "stock" ? "bg-white shadow-sm" : "text-zinc-500"}`}>
              <Boxes className="h-4 w-4" /> Barang Stok
            </button>
            <button type="button" onClick={() => setPurchaseType("non_stock")} className={`flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold ${form.purchase_type === "non_stock" ? "bg-white shadow-sm" : "text-zinc-500"}`}>
              <ReceiptText className="h-4 w-4" /> Biaya / Non-Stok
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div><label className="overline">Outlet</label><select data-testid="po-outlet" value={form.outlet_id} onChange={(event) => setForm({ ...form, outlet_id: event.target.value })} className="mt-1 w-full border border-border px-3 py-2"><option value="">— pilih —</option>{outlets.map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.name}</option>)}</select></div>
            <div><label className="overline">Supplier / Vendor</label><input data-testid="po-supplier" value={form.supplier} onChange={(event) => setForm({ ...form, supplier: event.target.value })} className="mt-1 w-full border border-border px-3 py-2" placeholder={form.purchase_type === "non_stock" ? "PLN, PAM, pemilik gedung, vendor..." : "Nama supplier"} /></div>
            {form.purchase_type === "stock" && <div><label className="overline">Skema Barang</label><select value={form.supply_scheme} onChange={(event) => setForm({ ...form, supply_scheme: event.target.value, payment_method: "payable" })} className="mt-1 w-full border border-border px-3 py-2"><option value="buy">Beli</option><option value="consignment">Kerjasama / Titip jual</option></select></div>}
            <div><label className="overline">Metode Pembayaran</label><select disabled={form.supply_scheme === "consignment"} value={form.payment_method} onChange={(event) => setForm({ ...form, payment_method: event.target.value })} className="mt-1 w-full border border-border px-3 py-2"><option value="payable">Utang / Belum Dibayar</option><option value="cash">Tunai</option><option value="bank">Bank / Transfer</option></select></div>
            <div><label className="overline">No. Referensi / Tagihan</label><input value={form.reference_no} onChange={(event) => setForm({ ...form, reference_no: event.target.value })} className="mt-1 w-full border border-border px-3 py-2" placeholder="Invoice, nomor pelanggan, nomor tagihan..." /></div>
          </div>

          {form.purchase_type === "stock" ? (
            <div className="mt-5">
              {form.supply_scheme === "consignment" && <p className="mb-3 border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">Titip jual: gunakan SKU khusus dengan stok awal 0. Vendor dan harga titip per SKU harus tetap. Harga titip adalah kewajiban per unit terjual; tidak ada pembayaran saat penerimaan. Rusak atau kedaluwarsa dapat diretur.</p>}
              <div className="mb-2 flex items-center justify-between"><div><div className="overline">Item Stok</div><div className="text-xs text-zinc-500">Penyimpanan transaksi ini akan menambah persediaan.</div></div><button onClick={addItem} data-testid="po-add-item" className="border border-border px-3 py-1.5 text-xs">+ Item</button></div>
              <div className="space-y-2">
                {form.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 items-end gap-2">
                    <select value={item.product_id} onChange={(event) => updateItem(index, "product_id", event.target.value)} className="col-span-6 border border-border px-2 py-2 text-sm" data-testid={`po-item-${index}-product`}><option value="">— produk —</option>{outletProducts.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}</select>
                    <input type="number" min="0.01" step="0.01" value={item.qty} onChange={(event) => updateItem(index, "qty", event.target.value)} className="col-span-2 border border-border px-2 py-2 text-sm" placeholder="Qty" />
                    <input type="number" min="0" step="0.01" value={item.cost} onChange={(event) => updateItem(index, "cost", event.target.value)} className="col-span-3 border border-border px-2 py-2 text-sm" placeholder="Harga beli" />
                    <button onClick={() => removeItem(index)} className="col-span-1 p-2 text-rose-600 hover:bg-rose-50"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                {form.items.length === 0 && <div className="border border-dashed border-border p-5 text-center text-xs text-zinc-500">Tambahkan barang yang masuk ke persediaan.</div>}
              </div>
              <div className="mt-3 flex justify-end text-sm"><span className="mr-3 text-zinc-500">Total</span><strong>{formatIDR(stockTotal)}</strong></div>
            </div>
          ) : (
            <div className="mt-5 border border-violet-200 bg-violet-50/40 p-4">
              <div className="mb-4 flex items-start gap-3"><FileText className="mt-0.5 h-5 w-5 text-violet-700" /><div><div className="font-semibold">Biaya non-stok</div><div className="text-xs leading-5 text-zinc-500">Tidak menambah stok. Jurnal langsung mendebit akun beban yang dipilih.</div></div></div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="md:col-span-2"><label className="overline">Akun Beban / COA</label><select value={form.expense_account_id} onChange={(event) => setForm({ ...form, expense_account_id: event.target.value })} className="mt-1 w-full border border-border bg-white px-3 py-2"><option value="">— pilih akun beban —</option>{expenseAccounts.map((account) => <option key={account.id} value={account.id}>{account.code} - {account.name}</option>)}</select></div>
                <div className="md:col-span-2"><label className="overline">Keterangan Biaya</label><input value={form.expense_description} onChange={(event) => setForm({ ...form, expense_description: event.target.value })} className="mt-1 w-full border border-border bg-white px-3 py-2" placeholder="Contoh: Tagihan listrik outlet Agustus 2026" /></div>
                <div><label className="overline">Nominal</label><input type="number" min="0.01" step="0.01" value={form.expense_amount} onChange={(event) => setForm({ ...form, expense_amount: event.target.value })} className="mt-1 w-full border border-border bg-white px-3 py-2" placeholder="0" /></div>
                <div><label className="overline">Jurnal</label><div className="mt-1 border border-border bg-white px-3 py-2 text-xs leading-5"><div><strong>Dr</strong> {selectedExpense ? `${selectedExpense.code} ${selectedExpense.name}` : "Akun Beban"}</div><div><strong>Cr</strong> {form.payment_method === "cash" ? "Kas" : form.payment_method === "bank" ? "Bank / QRIS" : "Utang Usaha"}</div></div></div>
              </div>
            </div>
          )}

          <div className="mt-4"><label className="overline">Catatan</label><textarea rows="2" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} className="mt-1 w-full border border-border px-3 py-2" /></div>

          <div className="mt-5 flex gap-2">
            <button onClick={() => setOpen(false)} className="flex-1 border border-border py-3">Batal</button>
            <button onClick={save} disabled={saving} data-testid="po-save-btn" className="flex-1 bg-[#002FA7] py-3 font-semibold text-white disabled:opacity-50">{saving ? "Menyimpan..." : "Simpan"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
